import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./Navbar.css";
import wcLogo from "../../assets/wealth-compounders.png";

const PORTAL_LINKS = [
  {
    label: "Login for clients",
    url: "https://portal.wealthcompounders.tech",
  },
  {
    label: "Login for Partners",
    url: "https://rpartner.wealthcompounders.tech/",
  },
];

const GetStartedDropdown = ({ mobile = false, onNavigate }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    setOpen(false);
  }, [onNavigate]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  return (
    <div
      ref={rootRef}
      className={`wc-ui-get-started${mobile ? " wc-ui-get-started-mobile" : ""}${open ? " wc-ui-get-started-open" : ""}`}
    >
      <button
        type="button"
        className="wc-ui-btn-gradient wc-ui-get-started-btn"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((prev) => !prev)}
      >
        Get Started
        <i
          className={`fas fa-chevron-down wc-ui-get-started-chevron${open ? " wc-ui-get-started-chevron-open" : ""}`}
          aria-hidden="true"
        />
      </button>
      <ul className="wc-ui-get-started-menu" role="menu" hidden={!open}>
        {PORTAL_LINKS.map((item) => (
          <li key={item.url} role="none">
            <a
              href={item.url}
              role="menuitem"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
            >
              <span className="wc-ui-get-started-menu-label">{item.label}</span>
              <i className="fas fa-arrow-up-right-from-square" aria-hidden="true" />
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
};

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const isHome =
    location.pathname === "/" || location.pathname.startsWith("/v");

  useEffect(() => {
    const handleScroll = () => setScrolled(window.pageYOffset > 100);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  /* Close menu on route change */
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  /* ── Smooth scroll for same-page anchors ── */
  const handleNavClick = (e, href) => {
    e.preventDefault();
    setMenuOpen(false);

    if (!isHome) {
      /* Navigate home first, then scroll after paint */
      navigate("/");
      setTimeout(() => {
        const target = document.querySelector(href);
        if (target)
          target.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 120);
    } else {
      const target = document.querySelector(href);
      if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    setMenuOpen(false);
    if (isHome) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  const handleAboutClick = (e) => {
    e.preventDefault();
    setMenuOpen(false);
    navigate("/about");
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleCalculatorClick = (e) => {
    e.preventDefault();
    setMenuOpen(false);
    navigate("/calculators");
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleContactClick = (e) => {
    e.preventDefault();
    setMenuOpen(false);
    navigate("/contact");
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleWealthClick = (e) => {
    e.preventDefault();
    setMenuOpen(false);
    navigate("/wealth");
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /* ── Active link helper ── */
  const isAboutActive = location.pathname === "/about";
  const isCalculatorActive = location.pathname === "/calculators";
  const isContactActive = location.pathname === "/contact";
  const isWealthActive = location.pathname === "/wealth";

  return (
    <header className="wc-ui-nav-header">
      <nav
        className={`wc-ui-navbar${scrolled ? " wc-ui-navbar-scrolled" : ""}`}
      >
        {/* Logo */}
        <a href="/" className="wc-ui-nav-logo" onClick={handleLogoClick}>
          <img
            src={wcLogo}
            alt="Wealth Compounders Logo"
            className="wc-ui-nav-logo-img"
          />
          <div className="wc-ui-nav-logo-text">
            <span className="wc-ui-nav-logo-top">WEALTH</span>
            <span className="wc-ui-nav-logo-bottom">COMPOUNDERS</span>
          </div>
        </a>

        {/* Nav menu */}
        <ul
          className={`wc-ui-nav-menu${menuOpen ? " wc-ui-nav-menu-active" : ""}`}
        >
           <li className="wc-ui-nav-item">
            <a
              href="/"
              className={`wc-ui-nav-link${location.pathname === "/" ? " wc-ui-nav-link-active" : ""}`}
              onClick={(e) => {
                e.preventDefault();
                setMenuOpen(false);
                navigate("/");
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            >
              Home
            </a>
          </li>
          {/* ── Services page ── */}
          <li className="wc-ui-nav-item">
            <a
              href="/services"
              className={`wc-ui-nav-link${location.pathname === "/services" ? " wc-ui-nav-link-active" : ""}`}
              onClick={(e) => {
                e.preventDefault();
                setMenuOpen(false);
                navigate("/services");
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            >
              Services
            </a>
          </li>

          <li className="wc-ui-nav-item">
            <a
              href="/wealth"
              className={`wc-ui-nav-link${isWealthActive ? " wc-ui-nav-link-active" : ""}`}
              onClick={handleWealthClick}
            >
              Products
            </a>
          </li>

          {/* <li className="wc-ui-nav-item">
            <a
              href="#wc-ui-testimony"
              className="wc-ui-nav-link"
              onClick={(e) => handleNavClick(e, "#wc-ui-testimony")}
            >
              Testimonials
            </a>
          </li> */}

          {/* ── About → navigates to /about page ── */}
          <li className="wc-ui-nav-item">
            <a
              href="/about"
              className={`wc-ui-nav-link${isAboutActive ? " wc-ui-nav-link-active" : ""}`}
              onClick={handleAboutClick}
            >
              About Us
            </a>
          </li>

          <li className="wc-ui-nav-item">
            <a
              href="/calculators"
              className={`wc-ui-nav-link${isCalculatorActive ? " wc-ui-nav-link-active" : ""}`}
              onClick={handleCalculatorClick}
            >
              Calculators
            </a>
          </li>

          <li className="wc-ui-nav-item">
            <a
              href="/contact"
              className={`wc-ui-nav-link${isContactActive ? " wc-ui-nav-link-active" : ""}`}
              onClick={handleContactClick}
            >
              Contact Us
            </a>
          </li>

          {/* Mobile CTA */}
          <li className="wc-ui-nav-item wc-ui-nav-item-mobile-cta">
            <GetStartedDropdown
              mobile
              onNavigate={location.pathname}
            />
          </li>
        </ul>

        {/* Desktop actions */}
        <div className="wc-ui-nav-actions">
          <GetStartedDropdown onNavigate={location.pathname} />
          <div
            className={`wc-ui-hamburger${menuOpen ? " wc-ui-hamburger-active" : ""}`}
            aria-label="Open menu"
            onClick={() => setMenuOpen((o) => !o)}
          >
            <span className="wc-ui-hamburger-bar"></span>
            <span className="wc-ui-hamburger-bar"></span>
            <span className="wc-ui-hamburger-bar"></span>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
